<template>
  <!-- 客户详情弹窗 -->
  <div v-if="modelValue" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="fixed inset-0 bg-black/50" @click="closeModal"></div>
    <div class="relative min-h-screen flex items-center justify-center p-4">
      <div class="relative bg-white rounded-2xl shadow-xl max-w-screen-2xl w-full max-h-[90vh] overflow-hidden">
        <!-- 头部 -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div class="flex items-center gap-4">
            <div :class="['w-12 h-12 rounded-full flex items-center justify-center', customerDetail?.type === '企业' ? 'bg-purple-100' : 'bg-green-100']">
              <svg v-if="customerDetail?.type === '企业'" class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
              </svg>
              <svg v-else class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ customerDetail?.name }}</h3>
              <div class="flex items-center gap-2 mt-1">
                <span :class="['px-2 py-0.5 rounded text-xs', getTypeClass(customerDetail?.type)]">{{ customerDetail?.type }}</span>
                <span v-if="customerDetail?.isKey" class="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">重点客户</span>
                <span class="text-xs text-slate-500">{{ customerDetail?.source }}</span>
              </div>
            </div>
          </div>
          <button @click="closeModal" class="p-1.5 text-slate-400 hover:text-slate-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="flex h-[calc(90vh-120px)]">
          <!-- 左侧导航 -->
          <div class="w-56 border-r border-slate-200 bg-slate-50 py-4">
            <nav class="space-y-1">
              <button
                v-for="tab in visibleDetailTabs"
                :key="tab.value"
                @click="activeDetailTab = tab.value"
                :class="[
                  'w-full px-4 py-2.5 text-left text-sm font-medium transition-colors flex items-center gap-2',
                  activeDetailTab === tab.value
                    ? 'bg-white text-primary-600 border-r-2 border-primary-500'
                    : 'text-slate-600 hover:bg-slate-100'
                ]"
              >
                <component :is="tab.icon" class="w-4 h-4" />
                {{ tab.label }}
              </button>
            </nav>
          </div>

          <!-- 右侧内容 -->
          <div ref="detailContentRef" class="flex-1 overflow-y-auto p-6" @scroll="handleDetailScroll">
            <!-- 基本信息 -->
            <div v-if="activeDetailTab === 'basic'" class="space-y-6">
              <div class="flex items-center justify-between">
                <h4 class="text-sm font-semibold text-slate-900">客户基本信息</h4>
                <button v-permission="'customer:edit'" @click="openEditCustomerModal" class="table-action-button is-primary" title="编辑">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                </button>
              </div>
              <!-- 个人信息卡片 -->
              <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">性别</div>
                  <div class="font-medium text-slate-900">{{ customerDetail?.gender === 0 ? '男' : customerDetail?.gender === 1 ? '女' : '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">联系电话</div>
                  <div class="font-medium text-slate-900">{{ customerDetail?.phone }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">备用电话</div>
                  <div class="font-medium text-slate-900">{{ customerDetail?.altPhone || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">身份证号</div>
                  <div class="font-medium text-slate-900">{{ customerDetail?.idCard ? customerDetail.idCard.replace(/(\d{4})\d+(\d{4})/, '$1**********$2') : '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">客户邮箱</div>
                  <div class="font-medium text-slate-900">{{ customerDetail?.email || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">客户等级</div>
                  <div class="font-medium text-slate-900">{{ customerDetail?.level || '普通' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">客户来源</div>
                  <div class="font-medium text-slate-900">{{ customerDetail?.source || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">所属销售</div>
                  <div class="flex flex-col items-start gap-1.5">
                    <div class="font-medium text-slate-900">{{ customerDetail?.salesPerson || customerDetail?.ownerUser?.displayName || customerDetail?.ownerUserName || '未分配' }}</div>
                    <button
                      v-if="hasCustomerPendingApproval"
                      type="button"
                      class="table-action-button is-purple"
                      title="查看审批详情"
                      @click.stop="openCustomerApprovalDetailModal"
                    >
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">注册时间</div>
                  <div class="font-medium text-slate-900">{{ customerDetail?.createdAt || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">累计消费</div>
                  <div class="font-medium text-primary-600">¥{{ (customerDetail?.totalSpent || 0).toLocaleString() }}</div>
                </div>
                <div class="col-span-2 bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">所在地区</div>
                  <div class="font-medium text-slate-900">{{ customerDetail?.address || '' }}</div>
                </div>
                <div class="col-span-2 bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">详细住址</div>
                  <div class="font-medium text-slate-900">{{ customerDetail?.detailAddress || '-' }}</div>
                </div>
                <div class="col-span-2 bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">标签</div>
                  <div class="flex flex-wrap gap-1 mt-1">
                    <span v-for="tag in parsedTags" :key="tag" class="px-2 py-0.5 rounded text-xs bg-primary-50 text-primary-700 border border-primary-200">{{ tag }}</span>
                    <span v-if="!parsedTags.length" class="text-slate-400">暂无标签</span>
                  </div>
                </div>
                <div class="col-span-2 bg-slate-50 rounded-lg p-4">
                  <div class="text-sm text-slate-500 mb-1">备注信息</div>
                  <div class="font-medium text-slate-900">{{ customerDetail?.remark || '暂无备注' }}</div>
                </div>
              </div>

            </div>

            <!-- 关联企业 -->
            <div v-if="activeDetailTab === 'enterprises'" class="space-y-4">
              <div class="flex justify-between items-center">
                <h4 class="text-sm font-semibold text-slate-900">关联企业列表</h4>
                <button v-permission="'customer:company:add'" @click="openAddEnterpriseModal" class="flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 text-sm">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  添加关联企业
                </button>
              </div>
              <div v-if="!relatedEnterprises.length && !isCompanyFetching" class="border border-dashed border-slate-200 rounded-lg py-10 text-center text-sm text-slate-500">
                暂无关联企业
              </div>
              <div v-else class="grid gap-4">
                <div v-for="ent in relatedEnterprises" :key="ent.id" class="border border-slate-200 rounded-lg overflow-hidden">
                  <div class="flex items-start justify-between p-4 bg-slate-50">
                    <div class="flex items-start gap-3">
                      <div class="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                        </svg>
                      </div>
                      <div>
                        <div class="font-medium text-slate-900">{{ ent.name }}</div>
                        <div class="text-sm text-slate-500 mt-1">{{ ent.relation }}</div>
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <span :class="['px-2 py-0.5 rounded text-xs', ent.status === 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700']">{{ ent.statusLabel }}</span>
                      <button v-permission="'customer:company:edit'" @click.stop="openEditEnterpriseModal(ent)" class="px-2 py-0.5 text-xs border border-slate-200 rounded text-slate-600 hover:bg-white">编辑</button>
                      <button v-permission="'customer:company:delete'" @click.stop="handleDeleteCompany(ent)" class="px-2 py-0.5 text-xs border border-red-200 rounded text-red-600 hover:bg-red-50">删除</button>
                    </div>
                  </div>
                  <div class="p-4">
                    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div class="bg-slate-50 rounded-lg p-3">
                        <div class="text-xs text-slate-400 mb-1">社会统一信用代码</div>
                        <div class="text-sm text-slate-700">{{ ent.creditCode || '-' }}</div>
                      </div>
                      <div class="bg-slate-50 rounded-lg p-3">
                        <div class="text-xs text-slate-400 mb-1">法人代表</div>
                        <div class="text-sm text-slate-700">{{ ent.legalPerson || '-' }}</div>
                      </div>
                      <div class="bg-slate-50 rounded-lg p-3">
                        <div class="text-xs text-slate-400 mb-1">注册资本</div>
                        <div class="text-sm text-slate-700">{{ ent.registeredCapital || '-' }}</div>
                      </div>
                      <div class="bg-slate-50 rounded-lg p-3">
                        <div class="text-xs text-slate-400 mb-1">成立日期</div>
                        <div class="text-sm text-slate-700">{{ ent.establishedDate || '-' }}</div>
                      </div>
                      <div class="col-span-2 bg-slate-50 rounded-lg p-3">
                        <div class="text-xs text-slate-400 mb-1">企业经营地址</div>
                        <div class="text-sm text-slate-700">{{ ent.companyAddress || '-' }}</div>
                      </div>
                      <div class="col-span-4 bg-slate-50 rounded-lg p-3">
                        <div class="text-xs text-slate-400 mb-1">经营范围</div>
                        <div class="text-sm text-slate-700">{{ ent.businessScope || '-' }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 客户方案 -->
            <div v-if="activeDetailTab === 'proposals'" class="space-y-4">
              <div class="flex justify-between items-center">
                <h4 class="text-sm font-semibold text-slate-900">客户方案列表</h4>
                <button v-permission="'plan:save'" @click="openProposalFormModal" class="flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 text-sm">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  新建方案
                </button>
              </div>
              <!-- 加载状态 -->
              <ProposalListTable
                :plans="customerProposals"
                :loading="isProposalLoading"
                :pagination="proposalPagination"
                :customer-linkable="false"
                compact-empty
                @detail="openPlanDetailModal"
                @edit="openProposalEditModal"
                @approval-detail="openApprovalDetailModal"
                @page-change="handleProposalPageChange"
                @size-change="handleProposalPageSizeChange"
              />
            </div>

            <!-- 订单信息 -->
            <div v-if="activeDetailTab === 'orders'" class="space-y-4">
              <div class="flex justify-between items-center">
                <h4 class="text-sm font-semibold text-slate-900">订单记录</h4>
              </div>
              <OrderListTable
                :orders="customerOrders"
                :loading="isOrderLoading"
                :pagination="orderPagination"
                :show-plan-column="true"
                :show-customer-column="false"
                :show-company-column="false"
                :show-approval-actions="true"
                :show-edit-action="true"
                :show-status-action="true"
                :show-pagination="true"
                approve-permission="order:approve"
                :edit-permission="null"
                :status-edit-permission="null"
                :approve-popover-order-id="approvePopoverOrderId"
                :reject-popover-order-id="rejectPopoverOrderId"
                :reject-reason="rejectReason"
                :approval-submitting="approvalSubmitting"
                compact-empty
                @detail="openOrderDetailModal"
                @plan-click="openOrderPlanDetail"
                @open-approve-popover="openCustomerOrderApprovePopover"
                @close-approve-popover="closeCustomerOrderApprovePopover"
                @open-reject-popover="openCustomerOrderRejectPopover"
                @close-reject-popover="closeCustomerOrderRejectPopover"
                @update:reject-reason="handleCustomerOrderRejectReasonChange"
                @approve="handleCustomerOrderApprove"
                @edit="openCustomerOrderEditModal"
                @approval-detail="openOrderApprovalDetailModal"
                @status-change="handleCustomerOrderStatusChange"
                @page-change="handleOrderPageChange"
                @size-change="handleOrderPageSizeChange"
              />
              <div v-if="false" class="space-y-3">
                <div v-for="n in 3" :key="n" class="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  <div class="w-10 h-10 bg-slate-200 rounded-lg animate-pulse"></div>
                  <div class="flex-1">
                    <div class="h-4 w-32 bg-slate-200 rounded animate-pulse mb-2"></div>
                    <div class="h-3 w-24 bg-slate-200 rounded animate-pulse"></div>
                  </div>
                  <div class="h-4 w-20 bg-slate-200 rounded animate-pulse"></div>
                  <div class="h-6 w-16 bg-slate-200 rounded-full animate-pulse"></div>
                </div>
              </div>
              <!-- 空状态 -->
              <div v-if="false" class="border border-dashed border-slate-200 rounded-lg py-10 text-center text-sm text-slate-500">
                暂无订单数据
              </div>
              <!-- 数据表格 -->
              <div v-if="false" class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-slate-50">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500">订单信息</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500">所属方案</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500">销售人员</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500">执行人</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500">服务周期</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-slate-500">订单金额</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-slate-500">状态</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-slate-500">创建时间</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-slate-500">操作</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-200">
                    <tr v-for="order in customerOrders" :key="order.id" class="hover:bg-slate-50">
                      <td class="px-4 py-3">
                        <div class="flex items-center gap-3">
                          <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                            </svg>
                          </div>
                          <div>
                            <div class="font-medium text-slate-900">{{ order.productNameSnapshot }}</div>
                            <div class="text-xs text-slate-500">{{ order.orderNo }}</div>
                          </div>
                        </div>
                      </td>
                      <td class="px-4 py-3 text-sm text-slate-600">
                        <span v-if="order.planName" class="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs">{{ order.planName }}</span>
                        <span v-else class="text-slate-400">-</span>
                      </td>
                      <td class="px-4 py-3 text-sm text-slate-600">
                        <div class="flex items-center gap-2">
                          <div class="w-6 h-6 bg-primary-50 rounded-full flex items-center justify-center">
                            <svg class="w-3 h-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                            </svg>
                          </div>
                          <span>{{ order.ownerUserName || '-' }}</span>
                        </div>
                      </td>
                      <td class="px-4 py-3 text-sm text-slate-600">{{ order.executorName || '-' }}</td>
                      <td class="px-4 py-3 text-sm text-slate-600">
                        {{ order.serviceStart }} ~ {{ order.serviceEnd }}
                      </td>
                      <td class="px-4 py-3 text-sm text-slate-900 text-right font-medium">¥{{ order.amount?.toLocaleString() || '0' }}</td>
                      <td class="px-4 py-3 text-center">
                        <span :class="['px-2 py-1 rounded-full text-xs font-medium', orderStatusMap[order.status]?.class || 'bg-slate-100 text-slate-600']">
                          {{ orderStatusMap[order.status]?.text || '未知' }}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-sm text-slate-600 text-center">{{ order.createTime?.replace('T', ' ').slice(0, 19) || '-' }}</td>
                      <td class="px-4 py-3 text-center">
                        <div class="flex justify-center gap-1">
                          <button @click="openOrderDetailModal(order)" class="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors" title="查看详情">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- 分页信息 -->
              <div v-if="false" class="flex items-center justify-between text-sm text-slate-500">
                <span>共 <span class="font-medium">{{ orderPagination.total }}</span> 条</span>
                <span>第 <span class="font-medium">{{ orderPagination.page }}</span> / <span class="font-medium">{{ orderPagination.pages }}</span> 页</span>
              </div>
            </div>

            <!-- 客户资料 -->
            <div v-if="activeDetailTab === 'documents'" class="space-y-6">
              <div class="flex justify-between items-center">
                <h4 class="text-sm font-semibold text-slate-900">客户资料库</h4>
                <button
                  v-permission="'customer:material:add'"
                  @click="openMaterialCreateModal"
                  class="flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 text-sm"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                  </svg>
                  上传资料
                </button>
              </div>

              <div v-if="isMaterialTypesLoading || isMaterialListLoading" class="border border-dashed border-slate-200 rounded-lg py-10 text-center text-sm text-slate-500">
                正在加载资料配置...
              </div>
              <div v-else-if="!materialCategoryDisplays.length" class="border border-dashed border-slate-200 rounded-lg py-10 text-center text-sm text-slate-500">
                暂无资料配置
              </div>
              <div v-else class="space-y-6">
                <div v-for="category in materialCategoryDisplays" :key="category.categoryCode" class="space-y-3">
                  <h5 class="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <span class="inline-flex h-2 w-2 rounded-full bg-primary-500"></span>
                    {{ category.categoryName }}
                  </h5>
                  <div class="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <div
                      v-for="item in category.items"
                      :key="item.definition.code"
                      class="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      @click="openMaterialDetailModal(item.definition, item.material)"
                    >
                      <div class="flex items-start justify-between gap-3">
                        <div class="flex-1 min-w-0">
                          <div class="font-medium text-slate-900 truncate flex items-center gap-2">
                            <span>{{ item.material?.name || item.definition.name }}</span>
                            <span v-if="item.definition.isRequired" class="text-xs px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200">必填</span>
                          </div>
                          <div class="text-xs text-slate-500 mt-1 truncate">
                            <span v-if="item.material?.name">{{ item.definition.name }}</span>
                            <span v-else>{{ item.definition.nameAlias || item.definition.description || '无描述' }}</span>
                          </div>
                        </div>
                        <div :class="['px-2 py-0.5 rounded text-xs', item.material ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500']">
                          {{ item.material ? '已建立' : '未建立' }}
                        </div>
                      </div>
                      <div class="mt-3 text-xs text-slate-500 space-y-1">
                        <div>文件数: {{ item.material?.fileCount || 0 }}</div>
                        <div v-if="item.definition.needExpireCheck">
                          有效期: {{ formatDate(item.material?.expireDate) }}
                        </div>
                        <div>最后上传: {{ formatDateTime(item.material?.lastUploadTime) }}</div>
                      </div>
                      <div class="mt-3">
                        <button v-permission="'customer:material:add'" class="w-full px-2 py-1.5 text-xs bg-primary-50 text-primary-600 rounded hover:bg-primary-100" @click.stop="openMaterialDetailModal(item.definition, item.material)">
                          编辑/上传
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="activeDetailTab === 'followup'" class="space-y-4">
              <div class="flex justify-between items-center">
                <h4 class="text-sm font-semibold text-slate-900">客户日志</h4>
                <button v-permission="'customer:follow:add'" @click="openFollowupModal(customer)" class="flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 text-sm">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  添加客户日志
                </button>
              </div>
              <div v-if="!followupRecords.length && !isFollowupFetching" class="border border-dashed border-slate-200 rounded-lg py-10 text-center text-sm text-slate-500">
                暂无客户日志
              </div>
              <div v-else class="relative">
                <div class="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                <div class="space-y-4">
                  <div v-for="record in followupRecords" :key="record.id" class="relative pl-10">
                    <div :class="['absolute left-2.5 w-3 h-3 rounded-full border-2 bg-white', getFollowupIconClass(record.type)]"></div>
                    <div class="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div class="flex items-start justify-between mb-2">
                        <div class="flex items-center gap-2">
                          <span :class="['px-2 py-0.5 rounded text-xs font-medium', getFollowupTypeClass(record.type)]">{{ record.type }}</span>
                          <span class="text-xs text-slate-500">{{ record.duration ? `${record.duration}分钟` : '-' }}</span>
                        </div>
                        <div class="flex items-center gap-1">
                          <span class="text-xs text-slate-500">{{ record.date || '-' }}</span>
                          <button v-permission="'customer:follow:edit'" @click="openEditFollowupModal(record)" class="p-0.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded" title="编辑">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                            </svg>
                          </button>
                          <button v-permission="'customer:follow:delete'" @click="handleDeleteFollowup(record)" class="p-0.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="删除">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-4 0h14"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <p class="text-sm text-slate-700">{{ record.content }}</p>
                      <div class="flex items-center gap-4 mt-3 text-xs text-slate-500">
                        <span>跟进人: {{ record.operator || '-' }}</span>
                        <span v-if="record.nextDate">下次跟进: {{ record.nextDate }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 资料维护弹窗 -->
    <div v-if="showMaterialModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeMaterialModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ activeMaterialDefinition?.name || '资料维护' }}</h3>
              <p class="text-xs text-slate-500">{{ activeMaterialDefinition?.nameAlias || activeMaterialDefinition?.description || '请选择资料类型' }}</p>
            </div>
            <button @click="closeMaterialModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-160px)]">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">资料类型 <span class="text-red-500">*</span></label>
                <select
                  v-model="selectedMaterialDefinitionCode"
                  @change="handleMaterialDefinitionChange"
                  class="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">请选择资料类型</option>
                  <optgroup v-for="category in materialTypeCategories" :key="category.categoryCode" :label="category.categoryName">
                    <option v-for="definition in category.materialDefinitions" :key="definition.code" :value="definition.code">
                      {{ definition.name }}
                    </option>
                  </optgroup>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">资料名称</label>
                <input
                  v-model="materialForm.name"
                  type="text"
                  class="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="请输入资料名称（默认使用类型名称）"
                >
              </div>
              <div v-if="activeMaterialDefinition?.needExpireCheck">
                <label class="block text-sm font-medium text-slate-700 mb-2">有效期 <span class="text-red-500">*</span></label>
                <el-date-picker v-model="materialForm.expireDate" type="date" class="w-full" format="YYYY年MM月DD日" value-format="YYYY-MM-DD" placeholder="请选择有效期" />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">客户可见</label>
                <div class="flex items-center">
                  <label class="form-switch">
                    <input
                      type="checkbox"
                      class="form-switch-input"
                      v-model="materialForm.visibleToCustomer"
                    >
                    <span class="form-switch-track"></span>
                  </label>
                  <span class="ml-2 text-sm text-slate-500">{{ materialForm.visibleToCustomer ? '是' : '否' }}</span>
                </div>
              </div>
            </div>

            <div v-if="materialFormFields.length">
              <h4 class="text-sm font-semibold text-slate-900 mb-3">表单信息</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div v-for="field in materialFormFields" :key="field.key">
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    {{ field.label }}
                    <span v-if="field.required" class="text-red-500">*</span>
                  </label>
                  <select
                    v-if="field.enum"
                    v-model="materialFormData[field.key]"
                    class="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">请选择</option>
                    <option v-for="option in field.enum" :key="option" :value="option">{{ option }}</option>
                  </select>
                  <select
                    v-else-if="field.type === 'boolean'"
                    v-model="materialFormData[field.key]"
                    class="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option :value="true">是</option>
                    <option :value="false">否</option>
                  </select>
                  <input
                    v-else-if="field.type === 'number' || field.type === 'integer'"
                    v-model="materialFormData[field.key]"
                    type="number"
                    class="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    :placeholder="field.placeholder || '请输入'"
                  >
                  <input
                    v-else-if="field.type === 'email'"
                    v-model="materialFormData[field.key]"
                    type="email"
                    class="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    :placeholder="field.placeholder || '请输入邮箱'"
                  >
                  <input
                    v-else-if="field.type === 'phone'"
                    v-model="materialFormData[field.key]"
                    type="tel"
                    class="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    :placeholder="field.placeholder || '请输入手机号'"
                  >
                  <textarea
                    v-else-if="field.type === 'textarea'"
                    v-model="materialFormData[field.key]"
                    rows="3"
                    class="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    :placeholder="field.placeholder || '请输入'"
                  ></textarea>
                  <el-date-picker
                    v-else-if="field.type === 'date' && field.format === 'date'"
                    v-model="materialFormData[field.key]"
                    type="date"
                    class="w-full"
                    format="YYYY年MM月DD日"
                    value-format="YYYY-MM-DD"
                    placeholder="请选择日期"
                  ></el-date-picker>
                  <el-date-picker
                    v-else-if="field.type === 'date' && field.format === 'datetime'"
                    v-model="materialFormData[field.key]"
                    type="datetime"
                    class="w-full"
                    format="YYYY年MM月DD日 HH:mm"
                    value-format="YYYY-MM-DD HH:mm:ss"
                    placeholder="请选择日期时间"
                  ></el-date-picker>
                  <el-time-picker
                    v-else-if="field.type === 'date' && field.format === 'time'"
                    v-model="materialFormData[field.key]"
                    class="w-full"
                    style="width: 100%"
                    format="HH:mm"
                    value-format="HH:mm:ss"
                    placeholder="请选择时间"
                  ></el-time-picker>
                  <input
                    v-else
                    v-model="materialFormData[field.key]"
                    type="text"
                    class="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    :placeholder="field.placeholder || '请输入'"
                  >
                </div>
              </div>
            </div>

            <div v-if="materialFileFields.length">
              <h4 class="text-sm font-semibold text-slate-900 mb-3">附件信息</h4>
              <div class="space-y-4">
                <div v-for="fileField in materialFileFields" :key="fileField.fieldCode" class="border border-slate-200 rounded-lg p-4">
                  <div class="flex items-center justify-between">
                    <div class="font-medium text-slate-900">{{ fileField.label || fileField.fieldCode }}</div>
                    <span v-if="fileField.required" class="text-xs px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200">必传</span>
                  </div>
                  <div class="mt-3 space-y-2">
                    <div
                      v-for="file in getMaterialFilesByField(fileField.fieldCode)"
                      :key="file.id"
                      class="material-file-row"
                    >
                      <div class="material-file-main">
                        <div class="material-file-name" :title="file.originalName">{{ file.originalName || '附件' }}</div>
                        <div class="material-file-meta">
                          <span>v{{ file.version || '-' }}</span>
                          <span>{{ formatFileSize(file.fileSize) || '-' }}</span>
                          <span>{{ formatDateTime(file.uploadTime) }}</span>
                        </div>
                      </div>
                      <div class="material-file-actions">
                        <FileAttachment
                          v-permission="'customer:material:preview'"
                          variant="button"
                          :files="toMaterialPreviewFile(file)"
                          :loader="loadMaterialPreviewFile"
                        />
                        <button
                          v-permission="'customer:material:download'"
                          class="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded"
                          @click.stop="handleDownloadMaterialFile(file)"
                        >
                          下载
                        </button>
                        <button
                          v-permission="'customer:material:delete'"
                          class="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                          @click.stop="handleDeleteMaterialFile(file)"
                        >
                          删除
                        </button>
                      </div>
                    </div>
                    <div v-if="!getMaterialFilesByField(fileField.fieldCode).length" class="text-xs text-slate-400">暂无文件</div>
                  </div>
                  <div class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div class="material-file-picker">
                      <input
                        :key="uploadFileStates[fileField.fieldCode]?.inputKey"
                        :ref="setMaterialFileInputRef(fileField.fieldCode)"
                        type="file"
                        class="sr-only"
                        :accept="PREVIEWABLE_ATTACHMENT_ACCEPT"
                        @change="handleMaterialFileChange(fileField.fieldCode, $event)"
                      >
                      <button
                        type="button"
                        class="material-file-picker-button"
                        @click="triggerMaterialFileInput(fileField.fieldCode)"
                      >
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                        </svg>
                        选择文件
                      </button>
                      <span class="material-file-picker-name" :title="uploadFileStates[fileField.fieldCode]?.file?.name || ''">
                        {{ uploadFileStates[fileField.fieldCode]?.file?.name || '未选择文件' }}
                      </span>
                    </div>
                    <input
                      v-model="uploadFileStates[fileField.fieldCode].changeReason"
                      type="text"
                      class="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="变更说明(可选)"
                    >
                    <button
                      @click="handleUploadMaterialFile(fileField.fieldCode)"
                      :disabled="!materialDetail?.id"
                      class="btn-primary disabled:opacity-50"
                    >
                      上传新版本
                    </button>
                  </div>
                  <div v-if="!materialDetail?.id" class="text-xs text-amber-600 mt-2">请先保存资料后再上传文件</div>
                </div>
              </div>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div class="text-xs text-slate-500">
              <span v-if="materialDetail?.id">资料ID: {{ materialDetail.id }}</span>
              <span v-else>尚未创建资料</span>
            </div>
            <div class="flex items-center justify-end gap-3">
              <button
                v-if="materialDetail?.id"
                @click="handleDeleteMaterial"
                class="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50"
              >
                删除资料
              </button>
              <button @click="closeMaterialModal" class="btn-secondary">取消</button>
              <button
                @click="handleSaveMaterial"
                :disabled="isMaterialSaving"
                class="btn-primary disabled:opacity-50"
              >
                {{ materialDetail?.id ? '保存更新' : '创建资料' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 删除资料弹窗 -->
    <div v-if="showDeleteMaterialModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeDeleteMaterialModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-sm w-full">
          <div class="px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">删除资料</h3>
          </div>
          <div class="p-6 text-sm text-slate-600">
            确认删除资料 "{{ materialToDelete?.name }}" 吗？
          </div>
          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="closeDeleteMaterialModal" class="btn-secondary">取消</button>
            <button @click="confirmDeleteMaterial" class="btn-danger-secondary">删除</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 删除文件弹窗 -->
    <div v-if="showDeleteMaterialFileModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeDeleteMaterialFileModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-sm w-full">
          <div class="px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">删除文件</h3>
          </div>
          <div class="p-6 text-sm text-slate-600">
            确认删除文件 "{{ materialFileToDelete?.originalName }}" 吗？
          </div>
          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="closeDeleteMaterialFileModal" class="btn-secondary">取消</button>
            <button @click="confirmDeleteMaterialFile" class="btn-danger-secondary">删除</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 跟进记录弹窗 -->
    <div v-if="showFollowupModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="showFollowupModal = false"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-lg w-full">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">{{ followupForm.id ? '编辑跟进记录' : '添加跟进记录' }}</h3>
            <button @click="showFollowupModal = false" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="p-6 space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">跟进方式 <span class="text-red-500">*</span></label>
              <div class="grid grid-cols-4 gap-2">
                <button
                  v-for="type in followupTypes"
                  :key="type.value"
                  @click="followupForm.type = type.value"
                  :class="[
                    'px-3 py-2 rounded-lg text-sm font-medium transition-colors border',
                    followupForm.type === type.value
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  ]"
                >
                  {{ type.label }}
                </button>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">跟进内容 <span class="text-red-500">*</span></label>
              <textarea v-model="followupForm.content" rows="4" class="form-textarea" placeholder="请输入跟进内容"></textarea>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">本次跟进时长(分钟)</label>
                <input v-model="followupForm.duration" type="number" class="form-input form-number w-full" placeholder="如: 30">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">下次跟进时间</label>
                <el-date-picker v-model="followupForm.nextDate" type="date" class="w-full" format="YYYY年MM月DD日" value-format="YYYY-MM-DD" placeholder="请选择下次跟进时间" />
              </div>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="showFollowupModal = false" class="btn-secondary">取消</button>
            <button
              @click="handleSubmitFollowup"
              class="btn-primary disabled:opacity-60"
              :disabled="isFollowupSubmitting"
            >
              {{ isFollowupSubmitting ? '提交中...' : '保存' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 删除跟进记录确认弹窗 -->
    <div v-if="showDeleteFollowupModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="showDeleteFollowupModal = false"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-sm w-full">
          <div class="px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">确认删除</h3>
          </div>
          <div class="p-6 text-sm text-slate-600">
            确认删除该条跟进记录吗？此操作不可撤销。
          </div>
          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="showDeleteFollowupModal = false" class="btn-secondary">取消</button>
            <button @click="confirmDeleteFollowup" class="btn-danger-secondary">删除</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 方案详情弹窗 -->
    <PlanDetailModal
      v-model:visible="showPlanDetailModal"
      :planData="planDetail"
    />

    <!-- 订单详情弹窗 -->
    <OrderDetailModal
      v-model:visible="showOrderDetailModal"
      :orderDetail="orderDetail"
      :traceList="orderTraceList"
      @receive="openCustomerOrderReceiveModal"
      @editTask="openCustomerOrderTaskEditModal"
      @edit="handleCustomerOrderDetailEdit"
      @approve="handleCustomerOrderDetailApprove"
      @approval-detail="openOrderApprovalDetailModal"
      @refreshDetail="handleCustomerOrderDetailRefresh"
      @executorChanged="handleCustomerOrderExecutorChanged"
    />

    <OrderFormModal
      v-model:visible="showOrderEditModal"
      :isEdit="true"
      :editData="editOrderData"
      @success="handleCustomerOrderSuccess"
    />

    <div v-if="showTaskEditModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeCustomerOrderTaskEditModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">编辑任务</h3>
            <button @click="closeCustomerOrderTaskEditModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="p-6">
            <div class="mb-6 p-4 bg-slate-50 rounded-lg space-y-2">
              <div class="flex items-center gap-3">
                <span class="text-sm font-medium text-slate-500 w-20">任务名称</span>
                <span class="text-sm text-slate-900">{{ selectedTask?.name }}</span>
              </div>
              <div v-if="selectedTask?.type" class="flex items-center gap-3">
                <span class="text-sm font-medium text-slate-500 w-20">任务类型</span>
                <span class="text-sm text-slate-900">{{ selectedTask?.type }}</span>
              </div>
              <div v-if="selectedTask?.assigneeName" class="flex items-center gap-3">
                <span class="text-sm font-medium text-slate-500 w-20">负责人</span>
                <div class="flex items-center gap-2">
                  <div class="w-6 h-6 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-xs font-medium">
                    {{ selectedTask?.assigneeName?.charAt(0) }}
                  </div>
                  <span class="text-sm text-slate-900">{{ selectedTask?.assigneeName }}</span>
                </div>
              </div>
              <div v-if="selectedTask?.startTime" class="flex items-center gap-3">
                <span class="text-sm font-medium text-slate-500 w-20">开始时间</span>
                <span class="text-sm text-slate-900">{{ $formatDateTime(selectedTask?.startTime) }}</span>
              </div>
              <div v-if="selectedTask?.endTime" class="flex items-center gap-3">
                <span class="text-sm font-medium text-slate-500 w-20">结束时间</span>
                <span class="text-sm text-slate-900">{{ $formatDateTime(selectedTask?.endTime) }}</span>
              </div>
            </div>

            <el-form ref="taskFormRef" :model="taskFormData" :rules="taskRules" label-width="100px">
              <el-form-item label="任务状态" prop="status">
                <el-select v-model="taskFormData.status" placeholder="请选择任务状态" class="w-full">
                  <el-option label="待开始" :value="0" />
                  <el-option label="进行中" :value="1" />
                  <el-option label="已完成" :value="2" />
                  <el-option label="已暂停" :value="3" />
                </el-select>
              </el-form-item>

              <el-form-item label="任务进度" prop="progress">
                <div class="flex items-center gap-3 w-full">
                  <el-slider v-model="taskFormData.progress" :min="0" :max="100" class="flex-1" />
                  <el-input-number
                    v-model="taskFormData.progress"
                    :min="0"
                    :max="100"
                    style="width: 100px"
                  />
                  <span class="text-slate-600">%</span>
                </div>
              </el-form-item>

              <el-form-item label="备注" prop="remark">
                <el-input
                  v-model="taskFormData.remark"
                  type="textarea"
                  :rows="4"
                  placeholder="请输入备注信息"
                />
              </el-form-item>
            </el-form>
          </div>

          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="closeCustomerOrderTaskEditModal" class="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">取消</button>
            <button :disabled="taskSubmitting" @click="handleCustomerOrderTaskSubmit" class="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
              {{ taskSubmitting ? '保存中...' : '保存' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showBillingReceiveModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeCustomerOrderReceiveModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-md w-full">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">账单收款</h3>
            <button @click="closeCustomerOrderReceiveModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="p-6">
            <el-form ref="receiveFormRef" :model="receiveFormData" :rules="receiveRules" label-width="100px">
              <el-form-item label="应收金额">
                <div class="text-lg font-medium text-slate-900">¥{{ selectedBilling?.amount?.toLocaleString() }}</div>
              </el-form-item>
              <el-form-item label="已收金额">
                <div class="text-slate-600">¥{{ selectedBilling?.paidAmount?.toLocaleString() || '0' }}</div>
              </el-form-item>
              <el-form-item label="待收金额">
                <div class="text-amber-600 font-medium">¥{{ receiveRemainingAmount.toLocaleString() }}</div>
              </el-form-item>
              <el-form-item label="收款金额" prop="paidAmount">
                <el-input-number
                  v-model="receiveFormData.paidAmount"
                  :min="0.01"
                  :max="receiveRemainingAmount"
                  :precision="2"
                  :step="100"
                  style="width: 200px"
                />
              </el-form-item>
              <el-form-item label="支付渠道" prop="payChannel">
                <el-select v-model="receiveFormData.payChannel" placeholder="请选择支付渠道" style="width: 200px">
                  <el-option v-for="channel in payChannelOptions" :key="channel.value" :label="channel.label" :value="channel.value" />
                </el-select>
              </el-form-item>
            </el-form>
          </div>

          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="closeCustomerOrderReceiveModal" class="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">取消</button>
            <button :disabled="receiveSubmitting" @click="handleCustomerOrderReceiveSubmit" class="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">确认收款</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 新建方案弹窗 -->
    <ProposalFormModal
      v-model:visible="showProposalFormModal"
      :isEdit="isProposalEdit"
      :editData="proposalFormEditData"
      @success="handleProposalSuccess"
    />

    <!-- 审批详情弹窗 -->
    <ApprovalDetailModal
      v-model="showApprovalDetailModal"
      :instance-id="selectedApprovalInstanceId"
      :type="selectedApprovalType"
      title="审批详情"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { getPlanPage } from '@/api/plan'
import { getOrderPage, getOrderDetail, updateOrderStatus, approveOrder } from '@/api/order'
import { receiveBilling } from '@/api/billing'
import { getTraceList } from '@/api/trace'
import { updateTask } from '@/api/task'
import {
  getCompanyPage,
  getCustomer,
  getCustomerFollowPage,
  createCustomerFollow,
  updateCustomerFollow,
  deleteCustomerFollow,
  getCustomerMaterialTypes,
  getCustomerMaterials,
  getCustomerMaterialDetail,
  getCustomerMaterialDetailById,
  submitCustomerMaterial,
  uploadCustomerMaterialFile,
  deleteCustomerMaterial,
  deleteCustomerMaterialFile
} from '@/api/customer'
import { uploadFile } from '@/api/file'
import PlanDetailModal from '@/components/PlanDetailModal.vue'
import OrderDetailModal from '@/components/OrderDetailModal.vue'
import OrderFormModal from '@/components/OrderFormModal.vue'
import ProposalFormModal from '@/components/ProposalFormModal.vue'
import ApprovalDetailModal from '@/components/ApprovalDetailModal.vue'
import OrderListTable from '@/components/OrderListTable.vue'
import ProposalListTable from '@/components/ProposalListTable.vue'
import FileAttachment from '@/components/FileAttachment.vue'
import { normalizeCustomerMaterialFile, withBlobPreview } from '@/utils/filePreview'
import { PREVIEWABLE_ATTACHMENT_ACCEPT, PREVIEWABLE_ATTACHMENT_ACCEPT_LABEL, isPreviewableAttachmentFile } from '@/utils/fileAccept'

// Icon components
const IconUser = {
  template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>`
}
const IconBuilding = {
  template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>`
}
const IconDocument = {
  template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>`
}
const IconShopping = {
  template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>`
}
const IconFile = {
  template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>`
}
const IconChat = {
  template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/></svg>`
}

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  customer: { type: Object, default: null },
  visibleTabs: { type: Array, default: () => ['basic', 'enterprises', 'proposals', 'orders', 'documents', 'followup'] }
})

const emit = defineEmits(['update:modelValue', 'edit-customer', 'open-add-enterprise', 'open-edit-enterprise', 'delete-company', 'refresh', 'refresh-enterprises'])

// Tab state
const activeDetailTab = ref('basic')

// Refs
const detailContentRef = ref(null)

// Modal state
const showMaterialModal = ref(false)
const showDeleteMaterialModal = ref(false)
const showDeleteMaterialFileModal = ref(false)
const showFollowupModal = ref(false)
const showDeleteFollowupModal = ref(false)
const showPlanDetailModal = ref(false)
const showOrderDetailModal = ref(false)
const showOrderEditModal = ref(false)
const showTaskEditModal = ref(false)
const showBillingReceiveModal = ref(false)
const showProposalFormModal = ref(false)
const showApprovalDetailModal = ref(false)
const selectedApprovalInstanceId = ref(null)
const selectedApprovalType = ref('proposal')
const editOrderData = ref(null)
const selectedTask = ref(null)
const selectedBilling = ref(null)
const approvePopoverOrderId = ref(null)
const rejectPopoverOrderId = ref(null)
const rejectReason = ref('')
const approvalSubmitting = ref(false)
const taskFormRef = ref(null)
const receiveFormRef = ref(null)
const taskSubmitting = ref(false)
const receiveSubmitting = ref(false)
const isProposalEdit = ref(false)
const proposalFormCurrentPlan = ref(null)
const isFollowupFetching = ref(false)
const isCompanyFetching = ref(false)
const isFollowupSubmitting = ref(false)

// Loading states
const isMaterialTypesLoading = ref(false)
const isMaterialListLoading = ref(false)
const isMaterialSaving = ref(false)
const isProposalLoading = ref(false)
const isOrderLoading = ref(false)
const isCustomerDetailLoading = ref(false)

// Data refs
const relatedEnterprises = ref([])
const customerProposals = ref([])
const customerOrders = ref([])
const followupRecords = ref([])
const customerDetail = ref(null)
const planDetail = ref(null)
const orderDetail = ref(null)
const orderTraceList = ref([])

// Pagination
const followupPagination = reactive({
  page: 1,
  size: 10,
  total: 0,
  pages: 0
})

const companyPagination = reactive({
  page: 1,
  size: 20,
  total: 0,
  pages: 0
})

const proposalPagination = reactive({
  page: 1,
  size: 10,
  total: 0,
  pages: 0
})

const orderPagination = reactive({
  page: 1,
  size: 10,
  total: 0,
  pages: 0
})

const taskFormData = reactive({
  status: 0,
  progress: 0,
  remark: ''
})

const receiveFormData = reactive({
  paidAmount: 0,
  payChannel: ''
})

// Tab definitions
const detailTabs = [
  { label: '基本信息', value: 'basic', icon: IconUser },
  { label: '关联企业', value: 'enterprises', icon: IconBuilding },
  { label: '客户方案', value: 'proposals', icon: IconDocument },
  { label: '订单信息', value: 'orders', icon: IconShopping },
  { label: '客户资料', value: 'documents', icon: IconFile },
  { label: '跟进记录', value: 'followup', icon: IconChat }
]

const visibleTabsArr = computed(() => props.visibleTabs || ['basic', 'enterprises', 'proposals', 'orders', 'documents', 'followup'])
const visibleDetailTabs = computed(() => detailTabs.filter(tab => visibleTabsArr.value.includes(tab.value)))
const proposalFormEditData = computed(() => {
  if (isProposalEdit.value) {
    return proposalFormCurrentPlan.value
  }

  return {
    customerId: customerDetail.value?.id,
    customerName: customerDetail.value?.name || ''
  }
})

const receiveRemainingAmount = computed(() => {
  if (!selectedBilling.value) return 0
  return (selectedBilling.value.amount || 0) - (selectedBilling.value.paidAmount || 0)
})

// Material related
const materialTypeCategories = ref([])
const customerMaterials = ref([])
const selectedMaterialDefinitionCode = ref('')
const activeMaterialDefinition = ref(null)
const materialDetail = ref(null)
const materialSchema = ref(null)
const materialFileConfig = ref({ files: [] })
const materialModalMode = ref('create') // 'create' | 'edit'
const materialForm = reactive({
  definitionCode: '',
  name: '',
  expireDate: '',
  visibleToCustomer: false
})
const materialFormData = reactive({})
const uploadFileStates = reactive({})
const materialFileInputRefs = {}
const materialToDelete = ref(null)
const materialFileToDelete = ref(null)
let materialDetailRequestSeq = 0

const receiveRules = {
  paidAmount: [
    { required: true, message: '请输入收款金额', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (!value || value <= 0) {
          callback(new Error('收款金额必须大于0'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ],
  payChannel: [
    { required: true, message: '请选择支付渠道', trigger: 'change' }
  ]
}

const taskRules = {
  status: [
    { required: true, message: '请选择任务状态', trigger: 'change' }
  ],
  progress: [
    { required: true, message: '请输入任务进度', trigger: 'blur' },
    { type: 'number', min: 0, max: 100, message: '进度必须在0-100之间', trigger: 'blur' }
  ]
}

const payChannelOptions = [
  { label: '微信', value: 'WECHAT' },
  { label: '支付宝', value: 'ALIPAY' },
  { label: '银行转账', value: 'BANK_TRANSFER' },
  { label: '现金', value: 'CASH' },
  { label: '其他', value: 'OTHER' }
]

// Maps
const followupTypeLabelMap = { 0: '电话', 1: '微信', 2: '邮件', 3: '上门', 4: '其他' }
const followupTypeValueMap = { '电话': 0, '微信': 1, '邮件': 2, '上门': 3, '其他': 4 }

const orderStatusMap = {
  0: { text: '待开始', class: 'bg-slate-100 text-slate-600' },
  1: { text: '服务中', class: 'bg-blue-100 text-blue-700' },
  2: { text: '已完成', class: 'bg-green-100 text-green-700' },
  3: { text: '已取消', class: 'bg-red-100 text-red-700' }
}

// Followup types
const followupTypes = [
  { label: '电话', value: '电话' },
  { label: '微信', value: '微信' },
  { label: '邮件', value: '邮件' },
  { label: '上门', value: '上门' },
  { label: '其他', value: '其他' }
]

// Followup form
const followupForm = reactive({
  id: null,
  type: '电话',
  content: '',
  duration: '',
  nextDate: '',
  followTime: ''
})

const followupToDelete = ref(null)

// Helper functions
const normalizeFollowupTypeLabel = (label) => {
  if (label === '拜访') return '上门'
  return label || ''
}

const parseFollowupDuration = (remark) => {
  const value = String(remark ?? '').trim()
  if (!value) return ''
  if (/^\d+$/.test(value)) return Number(value)
  const match = value.match(/\d+/)
  return match ? Number(match[0]) : ''
}

const formatFollowupTimeLabel = (value) => {
  if (!value) return ''
  return String(value).replace('T', ' ').replace('Z', '')
}

const mapFollowupFromApi = (data = {}) => {
  const label = normalizeFollowupTypeLabel(data.followTypeName || followupTypeLabelMap[data.followType])
  return {
    id: data.id,
    type: label || '其他',
    date: formatFollowupTimeLabel(data.followTime),
    content: data.followContent || '',
    operator: data.createBy || '',
    nextDate: data.nextFollowTime ? String(data.nextFollowTime).split('T')[0] : '',
    duration: parseFollowupDuration(data.remark),
    followTimeRaw: data.followTime || ''
  }
}

const mapCompanyFromApi = (data = {}) => {
  return {
    id: data.id,
    name: data.companyName || '',
    relation: data.remark || '-',
    status: data.status,
    statusLabel: data.status === 1 ? '停用' : '正常',
    creditCode: data.socialCreditCode || '',
    legalPerson: data.legalPerson || '',
    registeredCapital: data.registeredCapital || '',
    establishedDate: data.establishmentDate || '',
    companyAddress: data.registeredAddress || '',
    businessScope: data.businessScope || '',
    licenseUrl: data.licenseUrl || '',
    licensePreviewUrl: data.licensePreviewUrl || data.licenseUrl || '',
    remark: data.remark || ''
  }
}

const mapFollowupToForm = (record = {}) => {
  Object.assign(followupForm, {
    id: record.id || null,
    type: record.type || '电话',
    content: record.content || '',
    duration: record.duration || '',
    nextDate: record.nextDate || '',
    followTime: record.followTimeRaw || ''
  })
}

// 生成东八区（UTC+8）的ISO时间格式
const toEastAsiaISO = (date = new Date()) => {
  const offset = 8 * 60 // 东八区偏移分钟数
  const utcTime = date.getTime() + (offset * 60 * 1000)
  const eastAsiaDate = new Date(utcTime)
  const isoString = eastAsiaDate.toISOString().slice(0, -1) // 移除末尾的 'Z'
  return isoString
}

const formatDateTimeFromDateInput = (value) => {
  if (!value) return undefined
  // 将日期字符串转换为东八区ISO格式（设置为当天00:00:00）
  const date = new Date(value + 'T00:00:00')
  return toEastAsiaISO(date)
}

const buildFollowupPayload = (options = {}) => {
  const { isUpdate = false } = options
  const followType = followupTypeValueMap[followupForm.type]
  return {
    customerId: customerDetail.value?.id,
    followType: Number.isInteger(followType) ? followType : undefined,
    followContent: followupForm.content?.trim(),
    followTime: followupForm.followTime || (isUpdate ? undefined : toEastAsiaISO()),
    nextFollowTime: formatDateTimeFromDateInput(followupForm.nextDate),
    remark: followupForm.duration ? String(followupForm.duration).trim() : undefined
  }
}

// Format functions
const formatDate = (value) => {
  if (!value) return '-'
  return String(value).slice(0, 10)
}

const formatDateTime = (value) => {
  if (!value) return '-'
  return String(value).replace('T', ' ').slice(0, 19)
}

const formatFileSize = (size) => {
  if (!size && size !== 0) return '-'
  const units = ['B', 'KB', 'MB', 'GB']
  let value = size
  let index = 0
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024
    index += 1
  }
  return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)}${units[index]}`
}

// Class getters
const getTypeClass = (type) => {
  return type === '企业' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
}

const getFollowupTypeClass = (type) => {
  const classes = {
    '电话': 'bg-blue-100 text-blue-700',
    '微信': 'bg-green-100 text-green-700',
    '邮件': 'bg-amber-100 text-amber-700',
    '上门': 'bg-purple-100 text-purple-700',
    '拜访': 'bg-purple-100 text-purple-700',
    '其他': 'bg-slate-100 text-slate-700'
  }
  return classes[type] || 'bg-slate-100 text-slate-700'
}

const getFollowupIconClass = (type) => {
  const classes = {
    '电话': 'border-blue-500 bg-blue-50',
    '微信': 'border-green-500 bg-green-50',
    '邮件': 'border-amber-500 bg-amber-50',
    '上门': 'border-purple-500 bg-purple-50',
    '拜访': 'border-purple-500 bg-purple-50',
    '其他': 'border-slate-500 bg-slate-50'
  }
  return classes[type] || 'border-slate-300'
}

// Material computed
const materialDefinitionMap = computed(() => {
  const map = new Map()
  materialTypeCategories.value.forEach(category => {
    (category.materialDefinitions || []).forEach(definition => {
      map.set(definition.code, definition)
    })
  })
  return map
})

// 解析标签（支持逗号分隔的字符串或数组）
const parsedTags = computed(() => {
  const tags = customerDetail.value?.tags
  if (!tags) return []
  if (Array.isArray(tags)) return tags.filter(t => t && t.trim())
  if (typeof tags === 'string') {
    return tags.split(',').map(t => t.trim()).filter(t => t)
  }
  return []
})

const hasCustomerPendingApproval = computed(() => {
  return customerDetail.value?.pendingApprovalInstanceId != null
})

const materialInstanceMap = computed(() => {
  const map = new Map()
  customerMaterials.value.forEach(category => {
    (category.materials || []).forEach(material => {
      map.set(material.definitionCode, material)
    })
  })
  return map
})

const materialCategoryDisplays = computed(() => {
  return customerMaterials.value
    .map(category => {
      const items = (category.materials || []).map(material => ({
        definition: materialDefinitionMap.value.get(material.definitionCode) || {
          code: material.definitionCode,
          name: material.definitionName,
          nameAlias: material.definitionName
        },
        material
      }))
      return { ...category, items }
    })
    .filter(category => category.items.length)
})

const materialFormFields = computed(() => {
  const schema = materialSchema.value
  if (!schema || schema.type !== 'object' || !schema.properties) return []
  const requiredSet = new Set(schema.required || [])
  return Object.entries(schema.properties).map(([key, config = {}]) => ({
    key,
    label: config.title || config.label || key,
    type: config.type || 'string',
    format: config.format || '',
    enum: Array.isArray(config.enumValues) ? config.enumValues : null,
    required: requiredSet.has(key),
    placeholder: config.placeholder || ''
  }))
})

const materialFileFields = computed(() => {
  return materialFileConfig.value?.files || []
})

// Utility functions
const parseJsonSafely = (value, fallback) => {
  if (value === null || value === undefined || value === '') {
    return fallback
  }
  if (typeof value === 'object') {
    return value
  }
  try {
    return JSON.parse(value)
  } catch (error) {
    return fallback
  }
}

const getMaterialDownloadUrl = (file) => {
  return toMaterialPreviewFile(file).downloadSource
}

const toMaterialPreviewFile = (file) => withBlobPreview(normalizeCustomerMaterialFile(file), 'customer-material')

const getAuthHeaders = () => {
  const userStore = useUserStore()
  const headers = {}
  if (userStore.accessToken) {
    headers.Authorization = `${userStore.tokenType} ${userStore.accessToken}`
  }
  let deviceId = localStorage.getItem('deviceId')
  if (!deviceId) {
    deviceId = `WEB-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    localStorage.setItem('deviceId', deviceId)
  }
  headers['X-Device-Id'] = deviceId
  headers['X-Client-Type'] = 'web'
  return headers
}

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename || 'download'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const loadMaterialPreviewFile = async (file, options = {}) => {
  const source = options.purpose === 'download'
    ? file.downloadSource || file.downloadUrl
    : file.previewSource || file.url
  const response = await axios.get(source, {
    responseType: 'blob',
    headers: getAuthHeaders()
  })
  return {
    data: response.data,
    contentType: response?.headers?.['content-type'] || response.data?.type || file.contentType
  }
}

// Modal control
const closeModal = () => {
  emit('update:modelValue', false)
}

const openEditCustomerModal = () => {
  if (!customerDetail.value?.id) {
    ElMessage.warning('请先选择客户')
    return
  }
  emit('edit-customer', customerDetail.value)
}

// Data loading
const loadCompanies = async () => {
  const customerId = customerDetail.value?.id
  if (!customerId) return
  isCompanyFetching.value = true
  try {
    const payload = {
      customerId,
      page: companyPagination.page,
      size: companyPagination.size
    }
    const result = await getCompanyPage(payload)
    companyPagination.page = result?.current || companyPagination.page
    companyPagination.size = result?.size || companyPagination.size
    companyPagination.total = result?.total || 0
    companyPagination.pages = result?.pages || 0
    relatedEnterprises.value = (result?.records || []).map(mapCompanyFromApi)
  } catch (error) {
    ElMessage.error(error?.message || '获取企业列表失败')
  } finally {
    isCompanyFetching.value = false
  }
}

const loadCustomerProposals = async () => {
  const customerId = customerDetail.value?.id
  if (!customerId) return
  isProposalLoading.value = true
  try {
    const payload = {
      customerId,
      page: proposalPagination.page,
      size: proposalPagination.size
    }
    const result = await getPlanPage(payload)
    proposalPagination.page = result?.current || proposalPagination.page
    proposalPagination.size = result?.size || proposalPagination.size
    proposalPagination.total = result?.total || 0
    proposalPagination.pages = result?.pages || 0
    customerProposals.value = result?.records || []
  } catch (error) {
    ElMessage.error(error?.message || '获取方案列表失败')
  } finally {
    isProposalLoading.value = false
  }
}

const fetchCustomerDetail = async (id) => {
  if (!id) return
  isCustomerDetailLoading.value = true
  try {
    const detail = await getCustomer(id)
    customerDetail.value = detail
  } catch (error) {
    console.error('获取客户详情失败:', error)
  } finally {
    isCustomerDetailLoading.value = false
  }
}

const loadCustomerOrders = async () => {
  const customerId = customerDetail.value?.id
  if (!customerId) return
  isOrderLoading.value = true
  try {
    const payload = {
      customerId,
      page: orderPagination.page,
      size: orderPagination.size
    }
    const result = await getOrderPage(payload)
    orderPagination.page = result?.current || orderPagination.page
    orderPagination.size = result?.size || orderPagination.size
    orderPagination.total = result?.total || 0
    orderPagination.pages = result?.pages || 0
    customerOrders.value = result?.records || []
  } catch (error) {
    ElMessage.error(error?.message || '获取订单列表失败')
  } finally {
    isOrderLoading.value = false
  }
}

const loadFollowupRecords = async ({ append = false } = {}) => {
  const customerId = customerDetail.value?.id
  if (!customerId) return
  isFollowupFetching.value = true
  try {
    const payload = {
      customerId,
      page: followupPagination.page,
      size: followupPagination.size
    }
    const result = await getCustomerFollowPage(payload)
    followupPagination.page = result?.current || followupPagination.page
    followupPagination.size = result?.size || followupPagination.size
    followupPagination.total = result?.total || 0
    followupPagination.pages = result?.pages || 0
    const records = (result?.records || []).map(mapFollowupFromApi)
    followupRecords.value = append ? followupRecords.value.concat(records) : records
  } catch (error) {
    ElMessage.error(error?.message || '获取跟进记录失败')
  } finally {
    isFollowupFetching.value = false
  }
}

const loadMaterialTypes = async () => {
  if (isMaterialTypesLoading.value) return
  isMaterialTypesLoading.value = true
  try {
    const result = await getCustomerMaterialTypes()
    materialTypeCategories.value = Array.isArray(result) ? result : []
  } catch (error) {
    ElMessage.error(error?.message || '获取资料类型失败')
  } finally {
    isMaterialTypesLoading.value = false
  }
}

const loadCustomerMaterials = async () => {
  const customerId = customerDetail.value?.id
  if (!customerId) return
  isMaterialListLoading.value = true
  try {
    const result = await getCustomerMaterials(customerId)
    customerMaterials.value = Array.isArray(result) ? result : []
  } catch (error) {
    ElMessage.error(error?.message || '获取客户资料失败')
  } finally {
    isMaterialListLoading.value = false
  }
}

// Material form functions
const resetMaterialForm = () => {
  materialForm.definitionCode = ''
  materialForm.name = ''
  materialForm.expireDate = ''
  materialForm.visibleToCustomer = false
  selectedMaterialDefinitionCode.value = ''
  activeMaterialDefinition.value = null
  materialDetail.value = null
  materialSchema.value = null
  materialFileConfig.value = { files: [] }
  Object.keys(materialFormData).forEach(key => delete materialFormData[key])
  Object.keys(uploadFileStates).forEach(key => delete uploadFileStates[key])
}

const initMaterialFormData = (schema, formDataValue) => {
  const parsed = parseJsonSafely(formDataValue, {})
  Object.keys(materialFormData).forEach(key => delete materialFormData[key])
  if (schema?.properties) {
    Object.keys(schema.properties).forEach((key) => {
      materialFormData[key] = parsed?.[key] ?? schema.properties?.[key]?.default ?? ''
    })
  } else if (parsed && typeof parsed === 'object') {
    Object.assign(materialFormData, parsed)
  }
}

const initUploadFileStates = (fileConfig) => {
  Object.keys(uploadFileStates).forEach(key => delete uploadFileStates[key])
  const files = fileConfig?.files || []
  files.forEach((file) => {
    uploadFileStates[file.fieldCode] = {
      file: null,
      changeReason: '',
      inputKey: Date.now() + Math.random()
    }
  })
}

const loadMaterialDetail = async (definition, materialSummary = null) => {
  if (!definition || !customerDetail.value?.id) return
  const seq = ++materialDetailRequestSeq
  materialDetail.value = null
  const customerId = customerDetail.value.id
  let detail = null
  if (materialSummary) {
    try {
      detail = await getCustomerMaterialDetail(customerId, definition.code)
    } catch (error) {
      if (seq === materialDetailRequestSeq) {
        ElMessage.error(error?.message || '获取资料详情失败')
      }
      return
    }
  }
  // 丢弃旧请求的响应
  if (seq !== materialDetailRequestSeq) return
  materialDetail.value = detail
  const schema = parseJsonSafely(definition.formSchema, null)
  const fileConfig = parseJsonSafely(definition.fileConfig, { files: [] })
  materialSchema.value = schema
  materialFileConfig.value = fileConfig || { files: [] }
  initMaterialFormData(schema, detail?.formData)
  initUploadFileStates(fileConfig)
  materialForm.definitionCode = definition.code
  materialForm.name = detail?.name || definition.name || ''
  materialForm.expireDate = detail?.expireDate || materialSummary?.expireDate || ''
  materialForm.visibleToCustomer = detail?.visibleToCustomer ?? false
}

/**
 * 根据ID加载资料详情
 */
const loadMaterialDetailById = async (userMaterialId, definition) => {
  if (!userMaterialId || !definition) return
  const seq = ++materialDetailRequestSeq
  materialDetail.value = null
  try {
    const detail = await getCustomerMaterialDetailById(userMaterialId)
    // 丢弃旧请求的响应
    if (seq !== materialDetailRequestSeq) return
    materialDetail.value = detail
    const schema = parseJsonSafely(definition.formSchema, null)
    const fileConfig = parseJsonSafely(definition.fileConfig, { files: [] })
    materialSchema.value = schema
    materialFileConfig.value = fileConfig || { files: [] }
    initMaterialFormData(schema, detail?.formData)
    initUploadFileStates(fileConfig)
    materialForm.definitionCode = definition.code
    materialForm.name = detail?.name || definition.name || ''
    materialForm.expireDate = detail?.expireDate || ''
    materialForm.visibleToCustomer = detail?.visibleToCustomer ?? false
  } catch (error) {
    if (seq === materialDetailRequestSeq) {
      ElMessage.error(error?.message || '获取资料详情失败')
    }
  }
}

const openMaterialCreateModal = async () => {
  if (!customerDetail.value?.id) {
    ElMessage.warning('请先选择客户')
    return
  }
  await loadMaterialTypes()
  resetMaterialForm()
  materialModalMode.value = 'create'
  showMaterialModal.value = true
}

const openMaterialDetailModal = async (definition, materialSummary = null) => {
  if (!customerDetail.value?.id) {
    ElMessage.warning('请先选择客户')
    return
  }
  await loadMaterialTypes()
  // 从已刷新的 materialDefinitionMap 中获取完整的 definition（包含 formSchema、allowMultiple）
  const fullDefinition = definition?.code ? materialDefinitionMap.value.get(definition.code) : null
  selectedMaterialDefinitionCode.value = definition?.code || ''
  activeMaterialDefinition.value = fullDefinition || definition || null
  showMaterialModal.value = true

  if (materialSummary?.id) {
    // 有已有资料，编辑模式获取详情
    materialModalMode.value = 'edit'
    await loadMaterialDetailById(materialSummary.id, fullDefinition || definition || null)
  } else {
    // 无已有资料，新建模式加载表单
    materialModalMode.value = 'create'
    resetMaterialForm()
    activeMaterialDefinition.value = fullDefinition || definition
    const schema = parseJsonSafely((fullDefinition || definition)?.formSchema, null)
    const fileConfig = parseJsonSafely((fullDefinition || definition)?.fileConfig, { files: [] })
    materialSchema.value = schema
    materialFileConfig.value = fileConfig || { files: [] }
    initMaterialFormData(schema, null)
    initUploadFileStates(fileConfig)
    materialForm.definitionCode = (fullDefinition || definition)?.code || ''
    materialForm.name = (fullDefinition || definition)?.name || ''
  }
}

const closeMaterialModal = () => {
  showMaterialModal.value = false
  resetMaterialForm()
}

const handleMaterialDefinitionChange = async () => {
  const definition = materialDefinitionMap.value.get(selectedMaterialDefinitionCode.value)
  activeMaterialDefinition.value = definition || null
  if (!definition) {
    resetMaterialForm()
    showMaterialModal.value = true
    return
  }
  // 新建模式：直接加载表单配置
  if (materialModalMode.value === 'create') {
    if (hasMaterialFormData() && !confirm('切换资料类型将丢失已填写的数据，是否继续？')) {
      selectedMaterialDefinitionCode.value = activeMaterialDefinition.value?.code || ''
      return
    }
    resetMaterialForm()
    activeMaterialDefinition.value = definition
    const schema = parseJsonSafely(definition.formSchema, null)
    const fileConfig = parseJsonSafely(definition.fileConfig, { files: [] })
    materialSchema.value = schema
    materialFileConfig.value = fileConfig || { files: [] }
    initMaterialFormData(schema, null)
    initUploadFileStates(fileConfig)
    materialForm.definitionCode = definition.code
    materialForm.name = definition.name || ''
    return
  }
  // 编辑模式：检查是否有已有资料
  const summary = materialInstanceMap.value.get(definition.code)
  if (summary?.id) {
    await loadMaterialDetailById(summary.id, definition)
  } else {
    resetMaterialForm()
    activeMaterialDefinition.value = definition
    const schema = parseJsonSafely(definition.formSchema, null)
    const fileConfig = parseJsonSafely(definition.fileConfig, { files: [] })
    materialSchema.value = schema
    materialFileConfig.value = fileConfig || { files: [] }
    initMaterialFormData(schema, null)
    initUploadFileStates(fileConfig)
    materialForm.definitionCode = definition.code
    materialForm.name = definition.name || ''
  }
}

const hasMaterialFormData = () => {
  if (materialForm.name) return true
  if (materialForm.expireDate) return true
  if (Object.keys(materialFormData).some(key => materialFormData[key] !== '' && materialFormData[key] !== null && materialFormData[key] !== undefined)) return true
  if (Object.values(uploadFileStates).some(state => state?.file)) return true
  return false
}

const buildMaterialFilesPayload = () => {
  // 从 materialDetail.files 构建已有文件的提交 payload
  return (materialDetail.value?.files || [])
    .filter(file => file.isLatest)
    .map(file => ({
      fieldCode: file.fieldCode,
      fileKey: file.fileName || '',
      originalName: file.originalName || '',
      fileSize: file.fileSize || 0,
      changeReason: '复用已有文件'
    }))
    .filter(item => item.fieldCode && item.fileKey)
}

const validateMaterialForm = () => {
  if (!activeMaterialDefinition.value?.code) {
    ElMessage.warning('请选择资料类型')
    return false
  }
  if (activeMaterialDefinition.value?.needExpireCheck && !materialForm.expireDate) {
    ElMessage.warning('请填写有效期')
    return false
  }
  // 校验表单字段必填
  for (const field of materialFormFields.value) {
    if (field.required && (materialFormData[field.key] === '' || materialFormData[field.key] === null || materialFormData[field.key] === undefined)) {
      ElMessage.warning(`请填写${field.label}`)
      return false
    }
  }
  // 校验文件字段必填
  for (const fileField of materialFileFields.value) {
    if (fileField.required) {
      // 检查已有文件
      const existingFiles = getMaterialFilesByField(fileField.fieldCode)
      // 检查待上传的新文件（新建和编辑都要看）
      const pendingFile = uploadFileStates[fileField.fieldCode]?.file
      if (!existingFiles?.length && !pendingFile) {
        ElMessage.warning(`请上传${fileField.label || fileField.fieldCode}文件`)
        return false
      }
    }
  }
  return true
}

const handleSaveMaterial = async () => {
  if (!customerDetail.value?.id) {
    ElMessage.warning('请先选择客户')
    return
  }
  if (!validateMaterialForm()) return
  isMaterialSaving.value = true

  const isNew = !materialDetail.value?.id
  const uploadedFiles = []

  // 先上传所有待上传文件，获取 fileKey
  const uploadTasks = Object.entries(uploadFileStates)
    .filter(([, state]) => state?.file)
    .map(async ([fieldCode, state]) => {
      try {
        const result = await uploadFile(state.file, 'customer_material', 'private')
        if (result?.fileName) {
          uploadedFiles.push({
            fieldCode,
            fileKey: result.fileName,
            originalName: state.file.name,
            fileSize: state.file.size,
            contentType: state.file.type,
            changeReason: state.changeReason || ''
          })
        }
      } catch (error) {
        throw new Error(`文件 ${state.file.name} 上传失败`)
      }
    })

  if (uploadTasks.length > 0) {
    try {
      await Promise.all(uploadTasks)
    } catch (error) {
      isMaterialSaving.value = false
      ElMessage.error(error.message || '文件上传失败')
      return
    }
  }

  // 构建提交 payload
  const payload = {
    customerId: customerDetail.value.id,
    definitionCode: activeMaterialDefinition.value.code,
    name: materialForm.name || undefined,
    formData: JSON.stringify(materialFormData || {}),
    expireDate: materialForm.expireDate || undefined,
    visibleToCustomer: materialForm.visibleToCustomer,
    files: uploadedFiles
  }
  // 更新已有资料时，合并已有文件
  if (!isNew) {
    payload.files = [
      ...buildMaterialFilesPayload(),
      ...uploadedFiles
    ]
    payload.userMaterialId = materialDetail.value.id
  }

  try {
    const result = await submitCustomerMaterial(payload)
    materialDetail.value = result
    // 清除所有上传状态
    Object.values(uploadFileStates).forEach(state => {
      state.file = null
      state.changeReason = ''
      state.inputKey = Date.now() + Math.random()
    })
    ElMessage.success(isNew ? '资料已创建' : '资料已更新')
    await loadCustomerMaterials()
  } catch (error) {
    ElMessage.error(error?.message || '保存资料失败')
  } finally {
    isMaterialSaving.value = false
  }
}

const handleMaterialFileChange = (fieldCode, event) => {
  const file = event?.target?.files?.[0]
  if (!uploadFileStates[fieldCode]) return
  if (file && !isPreviewableAttachmentFile(file)) {
    ElMessage.warning(`仅支持${PREVIEWABLE_ATTACHMENT_ACCEPT_LABEL}`)
    uploadFileStates[fieldCode].file = null
    if (event?.target) event.target.value = ''
    return
  }
  uploadFileStates[fieldCode].file = file || null
}

const setMaterialFileInputRef = (fieldCode) => (el) => {
  if (el) {
    materialFileInputRefs[fieldCode] = el
  } else {
    delete materialFileInputRefs[fieldCode]
  }
}

const triggerMaterialFileInput = (fieldCode) => {
  materialFileInputRefs[fieldCode]?.click()
}

const handleUploadMaterialFile = async (fieldCode) => {
  if (!customerDetail.value?.id) {
    ElMessage.warning('请先选择客户')
    return
  }
  if (!materialDetail.value?.id) {
    ElMessage.warning('请先创建资料')
    return
  }
  const state = uploadFileStates[fieldCode]
  if (!state?.file) {
    ElMessage.warning('请选择文件')
    return
  }
  const formData = new FormData()
  formData.append('fieldCode', fieldCode)
  formData.append('file', state.file)
  if (state.changeReason) {
    formData.append('changeReason', state.changeReason)
  }
  try {
    const result = await uploadCustomerMaterialFile(customerDetail.value.id, materialDetail.value.id, formData)
    materialDetail.value = result
    uploadFileStates[fieldCode].file = null
    uploadFileStates[fieldCode].changeReason = ''
    uploadFileStates[fieldCode].inputKey = Date.now() + Math.random()
    await loadCustomerMaterials()
    ElMessage.success('文件上传成功')
  } catch (error) {
    ElMessage.error(error?.message || '文件上传失败')
  }
}

const getMaterialFilesByField = (fieldCode) => {
  const files = materialDetail.value?.files || []
  return files
    .filter(file => file.fieldCode === fieldCode)
    .sort((a, b) => (b.version || 0) - (a.version || 0))
}

const handleDownloadMaterialFile = async (file) => {
  const url = getMaterialDownloadUrl(file)
  if (!url) {
    ElMessage.error('下载失败，请稍后重试')
    return
  }
  try {
    const response = await axios.get(url, {
      responseType: 'blob',
      headers: getAuthHeaders()
    })
    downloadBlob(response.data, file?.originalName)
  } catch (error) {
    ElMessage.error('下载失败，请稍后重试')
  }
}

const handleDeleteMaterial = () => {
  if (!materialDetail.value?.id) return
  materialToDelete.value = {
    id: materialDetail.value.id,
    name: materialDetail.value.name || activeMaterialDefinition.value?.name || materialForm.definitionCode
  }
  showDeleteMaterialModal.value = true
}

const confirmDeleteMaterial = async () => {
  if (!materialToDelete.value || !customerDetail.value?.id) return
  try {
    await deleteCustomerMaterial(customerDetail.value.id, materialToDelete.value.id)
    ElMessage.success('资料已删除')
    showDeleteMaterialModal.value = false
    materialToDelete.value = null
    closeMaterialModal()
    await loadCustomerMaterials()
  } catch (error) {
    ElMessage.error(error?.message || '删除资料失败')
  }
}

const closeDeleteMaterialModal = () => {
  showDeleteMaterialModal.value = false
  materialToDelete.value = null
}

const handleDeleteMaterialFile = (file) => {
  if (!materialDetail.value?.id || !file?.id) return
  materialFileToDelete.value = file
  showDeleteMaterialFileModal.value = true
}

const confirmDeleteMaterialFile = async () => {
  if (!materialDetail.value?.id || !materialFileToDelete.value?.id) return
  try {
    await deleteCustomerMaterialFile(materialDetail.value.id, materialFileToDelete.value.id)
    ElMessage.success('文件已删除')
    showDeleteMaterialFileModal.value = false
    materialFileToDelete.value = null
    if (activeMaterialDefinition.value) {
      await loadMaterialDetail(activeMaterialDefinition.value, materialInstanceMap.value.get(activeMaterialDefinition.value.code))
    }
    await loadCustomerMaterials()
  } catch (error) {
    ElMessage.error(error?.message || '删除文件失败')
  }
}

const closeDeleteMaterialFileModal = () => {
  showDeleteMaterialFileModal.value = false
  materialFileToDelete.value = null
}

// Event handlers
const openAddEnterpriseModal = () => {
  if (!customerDetail.value?.id) {
    ElMessage.warning('请先选择客户')
    return
  }
  emit('open-add-enterprise')
}

const openEditEnterpriseModal = (company) => {
  emit('open-edit-enterprise', company)
}

const handleDeleteCompany = (company) => {
  emit('delete-company', company)
}

// Plan detail modal
const openPlanDetailModal = (plan) => {
  planDetail.value = plan
  showPlanDetailModal.value = true
}

// Order detail modal
const openOrderDetailModal = async (order) => {
  try {
    orderDetail.value = await getOrderDetail(order.id)
    orderTraceList.value = await getTraceList('ORDER', order.id)
    showOrderDetailModal.value = true
  } catch (error) {
    ElMessage.error('加载订单详情失败')
  }
}

const handleCustomerOrderDetailRefresh = async () => {
  if (!orderDetail.value?.id) {
    return
  }
  try {
    orderDetail.value = await getOrderDetail(orderDetail.value.id)
    orderTraceList.value = await getTraceList('ORDER', orderDetail.value.id)
    await loadCustomerOrders()
  } catch (error) {
    ElMessage.error(error?.message || '刷新订单详情失败')
  }
}

const openOrderPlanDetail = (order) => {
  planDetail.value = order?.planId ? { id: order.planId } : null
  showPlanDetailModal.value = true
}

const openCustomerOrderEditModal = async (order) => {
  try {
    editOrderData.value = await getOrderDetail(order.id)
    showOrderEditModal.value = true
  } catch (error) {
    ElMessage.error('加载订单详情失败')
  }
}

const handleCustomerOrderDetailEdit = (order) => {
  editOrderData.value = order
  showOrderDetailModal.value = false
  showOrderEditModal.value = true
}

const openCustomerOrderApprovePopover = (order) => {
  closeCustomerOrderRejectPopover()
  approvePopoverOrderId.value = order.id
}

const closeCustomerOrderApprovePopover = () => {
  approvePopoverOrderId.value = null
}

const openCustomerOrderRejectPopover = (order) => {
  closeCustomerOrderApprovePopover()
  rejectPopoverOrderId.value = order.id
  rejectReason.value = ''
}

const closeCustomerOrderRejectPopover = () => {
  rejectPopoverOrderId.value = null
  rejectReason.value = ''
}

const handleCustomerOrderRejectReasonChange = (value) => {
  rejectReason.value = value
}

const handleCustomerOrderApprove = async ({ order, approved }) => {
  if (!approved && !rejectReason.value?.trim()) {
    ElMessage.warning('请填写不通过原因')
    return
  }

  approvalSubmitting.value = true
  try {
    await approveOrder({
      orderId: order.id,
      approved,
      reason: approved ? '' : rejectReason.value.trim()
    })
    ElMessage.success(approved ? '已通过' : '已驳回')
    closeCustomerOrderApprovePopover()
    closeCustomerOrderRejectPopover()
    showOrderDetailModal.value = false
    await loadCustomerOrders()
  } catch (error) {
    ElMessage.error(error?.message || '审批操作失败')
  } finally {
    approvalSubmitting.value = false
  }
}

const handleCustomerOrderDetailApprove = async ({ orderId, approved, reason }) => {
  approvalSubmitting.value = true
  try {
    await approveOrder({ orderId, approved, reason })
    ElMessage.success(approved ? '已通过' : '已驳回')
    showOrderDetailModal.value = false
    await loadCustomerOrders()
  } catch (error) {
    ElMessage.error(error?.message || '审批操作失败')
  } finally {
    approvalSubmitting.value = false
  }
}

const handleCustomerOrderStatusChange = async ({ order, status }) => {
  try {
    await updateOrderStatus({ orderId: order.id, status })
    ElMessage.success('状态更新成功')
    await loadCustomerOrders()
  } catch (error) {
    ElMessage.error(error?.message || '状态更新失败')
  }
}

const handleOrderPageChange = (page) => {
  if (page < 1 || page > orderPagination.pages) return
  orderPagination.page = page
  loadCustomerOrders()
}

const handleOrderPageSizeChange = (size) => {
  orderPagination.size = size
  orderPagination.page = 1
  loadCustomerOrders()
}

const isAmountApprovalInProgress = (order) => {
  return Number(order?.pendingApprovalStatus) === 0 && order?.pendingApprovalInstanceId != null
}

const handleCustomerOrderSuccess = async (result) => {
  ElMessage.success(isAmountApprovalInProgress(result) ? '已提交订单金额变更审批，审批通过后生效' : '订单修改成功')
  showOrderEditModal.value = false
  await loadCustomerOrders()
  if (orderDetail.value?.id) {
    orderDetail.value = await getOrderDetail(orderDetail.value.id)
  }
}

const openCustomerOrderReceiveModal = (billing) => {
  selectedBilling.value = billing
  receiveFormData.paidAmount = receiveRemainingAmount.value
  receiveFormData.payChannel = ''
  showBillingReceiveModal.value = true
}

const closeCustomerOrderReceiveModal = () => {
  showBillingReceiveModal.value = false
}

const handleCustomerOrderReceiveSubmit = async () => {
  try {
    await receiveFormRef.value.validate()
  } catch {
    return
  }

  receiveSubmitting.value = true
  try {
    await receiveBilling({
      billingId: selectedBilling.value.id,
      paidAmount: receiveFormData.paidAmount,
      payChannel: receiveFormData.payChannel
    })
    ElMessage.success('收款成功')
    closeCustomerOrderReceiveModal()
    if (orderDetail.value?.id) {
      orderDetail.value = await getOrderDetail(orderDetail.value.id)
    }
  } catch (error) {
    ElMessage.error(error?.message || '收款失败')
  } finally {
    receiveSubmitting.value = false
  }
}

const openCustomerOrderTaskEditModal = (task) => {
  selectedTask.value = task
  taskFormData.status = task.status ?? 0
  taskFormData.progress = task.progress || 0
  taskFormData.remark = task.remark || ''
  showTaskEditModal.value = true
}

const closeCustomerOrderTaskEditModal = () => {
  showTaskEditModal.value = false
  if (taskFormRef.value) {
    taskFormRef.value.resetFields()
  }
}

const handleCustomerOrderTaskSubmit = async () => {
  try {
    await taskFormRef.value.validate()
  } catch {
    return
  }

  taskSubmitting.value = true
  try {
    await updateTask({
      id: selectedTask.value.id,
      orderId: selectedTask.value.orderId,
      status: taskFormData.status,
      progress: taskFormData.progress,
      remark: taskFormData.remark
    })
    ElMessage.success('任务更新成功')
    closeCustomerOrderTaskEditModal()
    if (orderDetail.value?.id) {
      orderDetail.value = await getOrderDetail(orderDetail.value.id)
    }
  } catch (error) {
    ElMessage.error(error?.message || '任务更新失败')
  } finally {
    taskSubmitting.value = false
  }
}

const handleCustomerOrderExecutorChanged = async () => {
  await handleCustomerOrderDetailRefresh()
}

// Proposal form modal
const openProposalFormModal = () => {
  if (!customerDetail.value?.id) {
    ElMessage.warning('请先选择客户')
    return
  }
  isProposalEdit.value = false
  proposalFormCurrentPlan.value = null
  showProposalFormModal.value = true
}

const openProposalEditModal = (plan) => {
  isProposalEdit.value = true
  proposalFormCurrentPlan.value = plan
  showProposalFormModal.value = true
}

const handleProposalSuccess = () => {
  // 刷新方案列表
  isProposalEdit.value = false
  proposalFormCurrentPlan.value = null
  loadCustomerProposals()
}

const handleProposalPageChange = (page) => {
  if (page < 1 || page > proposalPagination.pages) return
  proposalPagination.page = page
  loadCustomerProposals()
}

const handleProposalPageSizeChange = (size) => {
  proposalPagination.size = size
  proposalPagination.page = 1
  loadCustomerProposals()
}

// Approval detail modal
const openApprovalDetailModal = (plan) => {
  if (!plan.approvalInstanceId) {
    ElMessage.warning('该方案暂无审批实例')
    return
  }
  selectedApprovalType.value = 'proposal'
  selectedApprovalInstanceId.value = plan.approvalInstanceId
  showApprovalDetailModal.value = true
}

const openOrderApprovalDetailModal = (order) => {
  if (!order?.pendingApprovalInstanceId) {
    ElMessage.warning('暂无审批实例')
    return
  }
  selectedApprovalType.value = 'order'
  selectedApprovalInstanceId.value = order.pendingApprovalInstanceId
  showApprovalDetailModal.value = true
}

const openCustomerApprovalDetailModal = () => {
  if (!customerDetail.value?.pendingApprovalInstanceId) {
    ElMessage.warning('暂无审批实例')
    return
  }
  selectedApprovalType.value = 'customer'
  selectedApprovalInstanceId.value = customerDetail.value.pendingApprovalInstanceId
  showApprovalDetailModal.value = true
}

// Followup event handlers
const openFollowupModal = () => {
  if (!customerDetail.value?.id) {
    ElMessage.warning('请先选择客户')
    return
  }
  Object.assign(followupForm, { id: null, type: '电话', content: '', duration: '', nextDate: '', followTime: '' })
  showFollowupModal.value = true
}

const openEditFollowupModal = (record) => {
  mapFollowupToForm(record || {})
  showFollowupModal.value = true
}

const handleDeleteFollowup = (record) => {
  if (!record?.id) return
  followupToDelete.value = record
  showDeleteFollowupModal.value = true
}

const handleSubmitFollowup = async () => {
  if (!customerDetail.value?.id) {
    ElMessage.warning('请先选择客户')
    return
  }
  if (!followupForm.content?.trim()) {
    ElMessage.warning('请输入跟进内容')
    return
  }
  isFollowupSubmitting.value = true
  try {
    if (followupForm.id) {
      const payload = buildFollowupPayload({ isUpdate: true })
      await updateCustomerFollow({ ...payload, id: followupForm.id })
      ElMessage.success('更新成功')
    } else {
      const payload = buildFollowupPayload()
      await createCustomerFollow(payload)
      ElMessage.success('创建成功')
    }
    showFollowupModal.value = false
    followupPagination.page = 1
    await loadFollowupRecords()
  } catch (error) {
    ElMessage.error(error?.message || '操作失败')
  } finally {
    isFollowupSubmitting.value = false
  }
}

const confirmDeleteFollowup = async () => {
  const target = followupToDelete.value
  if (!target?.id) return
  try {
    await deleteCustomerFollow(target.id)
    ElMessage.success('删除成功')
    showDeleteFollowupModal.value = false
    followupToDelete.value = null
    followupPagination.page = 1
    await loadFollowupRecords()
  } catch (error) {
    ElMessage.error(error?.message || '删除失败')
  }
}

const handleDetailScroll = (event) => {
  if (activeDetailTab.value !== 'followup') return
  if (isFollowupFetching.value) return
  if (followupPagination.page >= followupPagination.pages) return
  const target = event?.target
  if (!target) return
  const threshold = 40
  const isBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold
  if (!isBottom) return
  followupPagination.page += 1
  loadFollowupRecords({ append: true })
}

// Watchers
watch(() => props.modelValue, (val) => {
  if (val) {
    activeDetailTab.value = visibleDetailTabs.value[0]?.value || 'basic'
    companyPagination.page = 1
    companyPagination.total = 0
    companyPagination.pages = 0
    relatedEnterprises.value = []
    followupPagination.page = 1
    followupPagination.total = 0
    followupPagination.pages = 0
    followupRecords.value = []
  }
})

watch(() => props.customer?.id, (customerId) => {
  if (customerId) {
    customerDetail.value = null
    fetchCustomerDetail(customerId)
  }
})

watch([activeDetailTab, () => props.customer], ([tab]) => {
  if (tab === 'enterprises' && customerDetail.value?.id) {
    companyPagination.page = 1
    loadCompanies()
    return
  }
  if (tab === 'proposals') {
    proposalPagination.page = 1
    loadCustomerProposals()
    return
  }
  if (tab === 'orders') {
    orderPagination.page = 1
    loadCustomerOrders()
    return
  }
  if (tab === 'documents') {
    loadMaterialTypes()
    loadCustomerMaterials()
    return
  }
  if (tab === 'followup') {
    followupPagination.page = 1
    loadFollowupRecords()
  }
})
</script>

<style scoped>
/* 时间选择器样式优化 */
:deep(.el-time-picker) {
  width: 100% !important;
}
:deep(.el-time-picker .el-input__wrapper) {
  border-radius: 0.5rem;
  border-color: #cbd5e1;
  padding: 0 11px;
}
:deep(.el-time-picker .el-input__wrapper:hover) {
  border-color: #2563eb;
}
:deep(.el-time-panel) {
  border-radius: 0.5rem;
  width: 180px !important;
}
:deep(.el-time-panel__content) {
  width: 180px !important;
}
:deep(.el-time-panel .el-time-spinner) {
  width: 180px !important;
}
:deep(.el-time-panel__footer) {
  padding: 4px 12px;
}
:deep(.el-time-panel__btn) {
  font-size: 12px;
  padding: 4px 8px;
}

.material-file-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

.material-file-main {
  min-width: 0;
  flex: 1;
}

.material-file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
}

.material-file-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
}

.material-file-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.material-file-picker {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 40px;
  overflow: hidden;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  transition: border-color .15s, box-shadow .15s;
}

.material-file-picker:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, .12);
}

.material-file-picker-button {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 100%;
  padding: 0 12px;
  border-right: 1px solid #cbd5e1;
  color: #334155;
  background: #f8fafc;
  font-size: 13px;
  font-weight: 500;
  transition: background .15s, color .15s;
}

.material-file-picker-button:hover {
  color: #1d4ed8;
  background: #eff6ff;
}

.material-file-picker-name {
  min-width: 0;
  overflow: hidden;
  padding: 0 12px;
  color: #64748b;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .material-file-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .material-file-actions {
    width: 100%;
    flex-wrap: wrap;
  }
}
</style>
